import AllesAutoLogo from '@allesauto/design/graphics/Alles-Auto-Logo-Purple.svg';
import { ProductType, SchemaTypes } from '@allesauto/graphql';
import { isCheckOutProcess } from '@allesauto/utils/funcs.ts';
import { useHandleHoverMenuCartAndUser } from '@allesauto/utils/hooks.ts';
import { Routes_DE as Routes } from '@allesauto/utils/routes';
import {
  Favorite20,
  Menu20,
  ShoppingCart20,
  User20,
} from '@carbon/icons-react';
import { Transition } from '@headlessui/react';
import { useRouter } from 'next/router';
import React, { FC, useMemo } from 'react';

import { getHeaderClassNames } from './common/classNames';
import HeaderCartMenu from './HeaderCartMenu';
import HeaderCartNotification, {
  CartNotificationVehicle,
} from './HeaderCartNotification';
import HeaderIcon from './HeaderIcon';
import HeaderMenu, { type INavigationMenu } from './HeaderMenu';
import HeaderMenuMobile from './HeaderMenuMobile';
import HeaderUserMenu from './HeaderUserMenu';
import NextLink from '../nextLink';

export { INavigationMenu };

export type LinkItemId =
  | 'contact'
  | 'faq'
  | 'help'
  | 'login'
  | 'register'
  | 'myaccount'
  | 'mygarage'
  | 'logout';

export interface LinkItem {
  id: LinkItemId;
  name: string;
  href: string;
  iconType: string;
}

export interface HeaderProps {
  hasMessageNotification?: boolean;
  numOfNotificationsFavorite?: number;
  isUserLoggedIn?: boolean;
  displayUserIcon?: boolean;
  displayCartIcon?: boolean;
  displayFavoritesIcon?: boolean;
  cartNotificationVehicle?: CartNotificationVehicle;
  navigation?: INavigationMenu[];
  footerNavigation?: {
    generalLinks: LinkItem[];
    accountLinks: LinkItem[];
  };
  isSticky?: boolean;
  showCartNotification?: boolean;
  hideCartNotification?: () => void;
  cartItems?: SchemaTypes.CartItem[];
  logoutUser?: () => void;
  context?: string;
  logo?: {
    src: string;
    alt: string;
    href?: string;
  };
  logoPosition?: 'left' | 'center';
  size?: 'default' | 'large';
  whiteLabel?: boolean;
  isSimpleHeader?: boolean;
}

const Header: FC<HeaderProps> = ({
  navigation,
  numOfNotificationsFavorite,
  isUserLoggedIn = false,
  displayUserIcon = true,
  displayCartIcon = true,
  displayFavoritesIcon = true,
  footerNavigation,
  hasMessageNotification = false,
  cartNotificationVehicle,
  isSticky = true,
  showCartNotification = false,
  hideCartNotification,
  cartItems,
  logoutUser,
  context,
  logo,
  logoPosition = 'center',
  size = 'default',
  whiteLabel = false,
  isSimpleHeader = false,
}) => {
  const urlPath = useRouter().asPath;
  const {
    scrollingDirection,
    open,
    setOpen,
    showHoverMenu,
    showUserMenu,
    showCartMenu,
    handleUserHoverMenu,
    handleCartHoverMenu,
    handleHoverMenu,
    isDeviceTypeMobileOrTablet,
    handleClose,
    userLoggedIn,
  } = useHandleHoverMenuCartAndUser(isUserLoggedIn);

  const isStickyHeader = useMemo(
    () => isSticky && !isSimpleHeader,
    [isSimpleHeader, isSticky]
  );

  const className = useMemo(
    () =>
      getHeaderClassNames({
        context,
        size,
        isSimpleHeader,
        logoPosition,
        isStickyHeader,
        scrollingDirection,
        open,
      }),
    [
      context,
      isSimpleHeader,
      isStickyHeader,
      logoPosition,
      open,
      scrollingDirection,
      size,
    ]
  );

  return (
    <>
      {isStickyHeader && (
        <div className={className.spacerClass}>
          {/*
           * A spacer that reserves just as much height in the page flow as the floating header needs,
           * to ensure that we're not hiding content at the top of the page and don't have to maintain
           * margins in each and every page layout
           */}
        </div>
      )}
      {!isSimpleHeader && isDeviceTypeMobileOrTablet && (
        <HeaderMenuMobile
          open={open}
          handleClose={handleClose}
          navigation={navigation}
          footerNavigation={footerNavigation}
          isLogged={userLoggedIn}
          hasMessageNotification={hasMessageNotification}
          onLogout={logoutUser}
          isWhiteLabel={whiteLabel}
        />
      )}
      <header className={className.headerClass} data-hide-for-print="">
        <nav aria-label="Top" className={className.navClass}>
          <div className={className.gridClass}>
            {navigation && footerNavigation && (
              <div className={className.headerMenuToggle}>
                <HeaderIcon
                  // link={ROUTES.FAVORITE}
                  icon={
                    isCheckOutProcess(urlPath) ? undefined : (
                      <Menu20 className="text-purple80 monochrome:text-brandColor" />
                    )
                  }
                  isLogged={userLoggedIn}
                  hasMessageNotification={hasMessageNotification}
                  isMenuIcon={true}
                  onClick={() => setOpen(true)}
                />
              </div>
            )}
            {/* Flyout menus */}
            {!isSimpleHeader && (
              <HeaderMenu
                className={String(className.headerMenu)}
                open={showHoverMenu}
                handleHover={handleHoverMenu}
                navigation={navigation}
              />
            )}
            {/* Logo */}
            <div className={className.logoContainerClass}>
              <NextLink href={logo?.href ?? Routes.ROOT} passHref={true}>
                {logo ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    className={className.logoClass}
                  />
                ) : (
                  <AllesAutoLogo className={className.logoClass} />
                )}
              </NextLink>
            </div>

            <div
              className={className.iconsContainerClass}
              data-hide-for-print=""
            >
              {/* Favorite - Hide from navigation until after launch AAA-1192*/}
              {displayFavoritesIcon && !isSimpleHeader && (
                <HeaderIcon
                  link={Routes.FAVORITES}
                  icon={<Favorite20 className={className.iconClass} />}
                  numOfNotification={numOfNotificationsFavorite}
                  isLogged={false}
                />
              )}
              {/* Cart Menu / Preview */}
              {displayCartIcon && !isSimpleHeader && (
                <>
                  <HeaderCartMenu
                    className={className.headerCartMenu}
                    open={showCartMenu}
                    items={cartItems ?? []}
                    handleHover={handleCartHoverMenu}
                    link={Routes.SHOPPING_CART}
                    icon={<ShoppingCart20 className={className.iconClass} />}
                    numOfNotification={
                      cartItems?.filter(
                        (obj) =>
                          obj.productType === ProductType.Vehicle ||
                          obj.productType === ProductType.AccessoryStandAlone
                      ).length
                    }
                    hideCartNotification={hideCartNotification}
                    whiteLabel={whiteLabel}
                  />
                  {/* Cart Notification */}
                  {showCartNotification && (
                    <HeaderCartNotification
                      open={showCartNotification}
                      vehicle={cartNotificationVehicle}
                      link={Routes.SHOPPING_CART}
                      icon={<ShoppingCart20 className={className.iconClass} />}
                      hideCartNotification={hideCartNotification}
                      whiteLabel={whiteLabel}
                    />
                  )}
                </>
              )}
              {/* User */}
              {displayUserIcon && !isSimpleHeader && (
                <HeaderUserMenu
                  className={String(className.headerUserMenu)}
                  open={showUserMenu}
                  link={
                    userLoggedIn ? Routes.ACCOUNT.GARAGE : Routes.ACCOUNT.LOGIN
                  }
                  icon={<User20 className={className.iconClass} />}
                  handleHover={handleUserHoverMenu}
                  isUserIcon={true}
                  isLogged={userLoggedIn}
                  onLogout={logoutUser}
                  hideCartNotification={hideCartNotification}
                />
              )}
            </div>
          </div>
        </nav>
      </header>
      <Transition
        show={showHoverMenu}
        enter="transition-opacity duration-400 ease-in"
        enterFrom="opacity-0"
        enterTo="opacity-40"
        leave="transition-opacity duration-400 ease-out"
        leaveFrom="opacity-40"
        leaveTo="opacity-0"
      >
        <div className={className.darkBgClass} />
      </Transition>
    </>
  );
};

export default Header;
